import { useEffect, useState } from 'react'
import { useDashboardStats } from '../../../hooks/useDashboardStats'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import IncompleteTotalsBanner from '../shared/IncompleteTotalsBanner'
import { apiService, type SystemHealth } from '../../../services/api'
import {
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  CogIcon,
} from '@heroicons/react/24/outline'

/** "Not readable" and "zero" must never render the same way. */
const UNAVAILABLE = '—'

const statValue = (value: number | null | undefined): string | number =>
  value === null || value === undefined ? UNAVAILABLE : value

/** Maps the backend's service words onto a colour without inventing a verdict. */
const serviceTone = (state: string) => {
  switch (state) {
    case 'connected':
      return 'text-green-600'
    case 'disabled':
      return 'text-gray-600'
    default:
      return 'text-red-600'
  }
}

export default function AdminDashboard() {
  const { stats, loading, error } = useDashboardStats()

  // Real organization count. `GET /api/organizations/hierarchy` returns every
  // district with its schools; there is no `total` field, so the count is the
  // array lengths. This replaces a hardcoded `'45' // Mock data - would come
  // from API` and its equally invented `+2` delta (#159).
  //
  // `/organizations/my-access` and `/organizations/navigation` cannot be used
  // here: both are scoped to the caller's own DistrictAdmin/SchoolAdmin rows,
  // which a platform ADMIN typically has none of, so they return empty.
  const [orgCount, setOrgCount] = useState<number | null>(null)
  const [orgError, setOrgError] = useState<string | null>(null)

  const [health, setHealth] = useState<SystemHealth | null>(null)
  const [healthError, setHealthError] = useState<string | null>(null)
  const [healthLoading, setHealthLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const [orgs, systemHealth] = await Promise.all([
        apiService
          .getOrganizationHierarchy()
          .catch(() => ({ error: 'Network Error', data: undefined })),
        apiService.getSystemHealth().catch(() => ({ error: 'Network Error', data: undefined })),
      ])

      if (cancelled) {
        return
      }

      if (orgs.data) {
        const districts = orgs.data.districts || []
        setOrgCount(
          districts.length +
            districts.reduce((sum, district) => sum + (district.schools?.length || 0), 0)
        )
        setOrgError(null)
      } else {
        // No fallback number. An unreadable count renders as an em dash.
        setOrgCount(null)
        setOrgError(orgs.error || 'Unavailable')
      }

      if (systemHealth.data) {
        setHealth(systemHealth.data)
        setHealthError(null)
      } else {
        setHealth(null)
        setHealthError(systemHealth.error || 'Unavailable')
      }

      setHealthLoading(false)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  const systemStats = [
    {
      name: 'Total Events',
      value: statValue(stats?.totalEvents),
      change: stats?.changes.totalEvents,
      icon: CalendarIcon,
    },
    {
      // Labelled for what the number is. It is `pagination.total` from an
      // unfiltered `GET /api/athletes` — every athlete on the platform, with no
      // activity filter of any kind. "Active" claimed a distinction the query
      // does not make, which is the same defect as a wrong number.
      name: 'Registered Athletes',
      value: statValue(stats?.totalAthletes),
      change: stats?.changes.totalAthletes,
      icon: UserGroupIcon,
    },
    {
      name: 'Results Recorded',
      value: statValue(stats?.totalResults),
      change: stats?.changes.totalResults,
      icon: ChartBarIcon,
    },
    {
      // Labelled for what the number actually counts. "Organizations" was
      // ambiguous over a hierarchy that holds districts *and* schools, and an
      // ambiguous label on a real number is the same defect as a wrong number.
      name: 'Districts & Schools',
      value: statValue(orgCount),
      icon: BuildingOffice2Icon,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">System Administration</h1>
        <p className="mt-1 text-sm text-gray-600">
          Monitor platform performance and manage system-wide settings
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {stats && <IncompleteTotalsBanner gaps={stats.gaps} />}

      {orgError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4" role="status">
          <p className="text-sm text-amber-800">
            The district and school count could not be loaded.
          </p>
        </div>
      )}

      {/* System Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {systemStats.map((stat, index) => (
          <StatCard
            key={index}
            name={stat.name}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
            loading={loading}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/*
          System Health — only the two services the backend actually probes.

          `GET /api/monitoring/health` runs a real `SELECT 1` against Postgres
          and a real Redis `PING`, and reports the result. Those two rows are
          therefore real.

          Dropped, because nothing backs them (#159):
          - "API Response Time 125ms" — the handler computes a `responseTime`
            but `HealthStatusSchema` does not declare it, so Fastify strips it
            before it reaches the client. There is no latency figure to show.
          - "Background Jobs 3 running" — no route exposes the job processor's
            state; there is no queue-depth endpoint anywhere.
        */}
        <DashboardCard
          title="System Health"
          loading={healthLoading}
          error={healthError ?? undefined}
        >
          {health ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Database</span>
                <span className={`text-sm font-medium ${serviceTone(health.services.database)}`}>
                  {health.services.database}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Redis Cache</span>
                <span className={`text-sm font-medium ${serviceTone(health.services.redis)}`}>
                  {health.services.redis}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Memory Used</span>
                <span className="text-sm font-medium text-gray-900">
                  {health.system.memory.percentage}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Errors (recent)</span>
                <span className="text-sm font-medium text-gray-900">{health.errors.recent}</span>
              </div>
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500">Health data unavailable.</p>
          )}
        </DashboardCard>

        {/*
          The "Recent Alerts" card was removed here.

          It rendered three hardcoded literals ("High Server Load", "New
          Organization Added — Jefferson High School District joined the
          platform", "System Backup Complete") that were pure invention: there
          is no alert or notification route in backend/src/routes/, and no
          Prisma model to persist one. Rather than keep an invented feed, the
          card is gone.

          If an alerts feed is wanted, the honest source is
          `/api/monitoring/monitoring/errors/stats`, which returns real
          `recentErrors[]` with levels — but that route is unauthenticated and
          its path is doubled by the router prefix, so wiring it into an admin
          surface is its own piece of work, not a rename of this card.
        */}

        {/* Admin Actions */}
        <DashboardCard title="Admin Actions">
          <div className="space-y-3">
            <button className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
              <CogIcon className="h-4 w-4 mr-2" />
              System Settings
            </button>
            <button className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
              <UserGroupIcon className="h-4 w-4 mr-2" />
              User Management
            </button>
            <button className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
              <BuildingOffice2Icon className="h-4 w-4 mr-2" />
              Organizations
            </button>
            <button className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
              <ChartBarIcon className="h-4 w-4 mr-2" />
              Analytics Dashboard
            </button>
          </div>
        </DashboardCard>
      </div>

      {/* Recent Activity */}
      <DashboardCard title="Recent Platform Activity" loading={loading} error={error ?? undefined}>
        {stats?.recentActivity && stats.recentActivity.length > 0 ? (
          <div className="space-y-4">
            {stats.recentActivity.map(activity => (
              <div
                key={activity.id}
                className="flex items-center space-x-4 py-2 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.description}</p>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-xs text-gray-400">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No recent activity to display</p>
        )}
      </DashboardCard>
    </div>
  )
}
